import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ForgeDatePickerModule, ForgeTextFieldModule } from "@tylertech/forge-angular";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CustomValidatorMessage, LgdErrorStateMatcher, LgdFormControl, LgdFormControlDateConfig, evaluate, } from "../lgdCommon";

@Component({
    selector: "lgd-date-picker",
    templateUrl: "./lgdDatePicker.tmpl.html",
    styleUrls: ["./lgdDatePicker.component.scss"],
    imports: [
        ForgeDatePickerModule,
        ForgeTextFieldModule,
        ReactiveFormsModule,
    ]
})
export class LgdDatePickerComponent implements OnInit, AfterViewInit, OnDestroy
{
    private readonly controlContainer = inject(ControlContainer);

    private readonly destructor$ = new Subject<void>();
    @ViewChild("input") readonly inputElement: ElementRef<HTMLInputElement>;

    @Input() readonly controlName: string | number;
    @Input() readonly label: string;
    @Input() readonly hideLabel: boolean;

    readonly errorStateMatcher = new LgdErrorStateMatcher();
    readonly placeHolder: string = "MM/dd/yyyy";

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<Date | string>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<Date | string>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlDateConfig>
    {
        return LgdFormControl.isLgdFormControl<LgdFormControlDateConfig>(this.formControl)
            ? this.formControl
            : undefined;
    }

    hasCustomValidators: boolean;
    customErrors: ReadonlyArray<CustomValidatorMessage>;

    ngOnInit(): void
    {
        if (this.lgdFormControl)
        {
            this.hasCustomValidators = !!this.lgdFormControl.data.customValidators;

            this.customErrors = this.hasCustomValidators
                ? this.lgdFormControl.data.customValidators.reduce(
                    (l, x) => l.concat(x.getErrorMessage(this.label)),
                    []
                )
                : [];
        }

        if (this.formControl.value)
        {
            const date = this.getDate(this.formControl.value);
            if (date !== null)
            {
                this.formControl.setValue(date);
            }
        }
    }

    ngAfterViewInit(): void
    {
        if (!this.inputElement)
        {
            console.warn(`LgdDatePickerComponent.ngAfterViewInit - ViewChild inputElement is not defined!`);
        }

        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus
                .pipe(takeUntil(this.destructor$))
                .subscribe((shouldFocus: boolean) =>
                {
                    if (shouldFocus)
                    {
                        /**
                         * Calling in a setTimeout to avoid a bug in our current @angular version.
                         */
                        setTimeout(() =>
                        {
                            this.inputElement.nativeElement.focus();
                        });
                    }
                });
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    get min(): Date
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.min) : undefined;
    }

    get max(): Date
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.max) : undefined;
    }

    private getDate(value: Date | string): Date
    {
        return this.isDate(value)
            ? value
            : new Date(Date.parse(value));
    }

    private isDate(value: unknown): value is Date
    {
        return value instanceof Date;
    }

    hasError(id: string): boolean
    {
        return !!this.formControl && this.formControl.hasError(id);
    }

    get isInvalid(): boolean
    {
        return this.formControl.touched && this.formControl.invalid;
    }

    get isRequired(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.required);
    }
}
